import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { fetchWithAuthRejectAllErrors } from '../../shared/fetch-json.util';
import { getEnvVar } from '../../utils/getEnvVar';

const initialState = {
  comments: {},
  modalErrorMessage: undefined,
  modalSuccessMessage: undefined,
  createErrorMessage: undefined
};

export const fetchComments = createAsyncThunk(
  'comments/fetchComments',
  async (_, { getState }) => {
    const strategyNumber = getState().phases.strategyNumber;
    const url = `${getEnvVar('VITE_GSS_API_URL')}/${strategyNumber}/comments`;
    const config = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    };

    const response = await fetchWithAuthRejectAllErrors(url, config);

    return response.json();
  }
);

export const fetchCreateComment = createAsyncThunk(
  'comments/create',
  async (comment, { getState, rejectWithValue }) => {
    const strategyNumber = getState().phases.strategyNumber;
    const url = `${getEnvVar('VITE_GSS_API_URL')}/${strategyNumber}/comments`;
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(comment)
    };

    try {
      return await fetchWithAuthRejectAllErrors(url, config).then((response) =>
        response.json()
      );
    } catch (error) {
      return rejectWithValue(await error.json());
    }
  }
);

export const fetchEditComment = createAsyncThunk(
  'comments/edit',
  async (comment, { rejectWithValue }) => {
    const url = `${getEnvVar('VITE_DMSM_API_URL')}/comments/${comment.commentId}`;
    const config = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(comment)
    };

    try {
      return await fetchWithAuthRejectAllErrors(url, config).then((response) =>
        response.json()
      );
    } catch (error) {
      return rejectWithValue(await error.json());
    }
  }
);

export const fetchDeleteComment = createAsyncThunk(
  'comments/delete',
  async (commentId, { rejectWithValue }) => {
    const url = `${getEnvVar('VITE_DMSM_API_URL')}/comments/${commentId}`;
    const config = {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      await fetchWithAuthRejectAllErrors(url, config);

      return { commentId };
    } catch (error) {
      return rejectWithValue(await error.json());
    }
  }
);

const clearModalMessages = (state) => {
  state.modalErrorMessage = undefined;
  state.modalSuccessMessage = undefined;
  state.createErrorMessage = undefined;
};

const commentSlice = createSlice({
  name: 'commentSlice',
  initialState,
  reducers: {
    clearLinksModalMessages: (state) => clearModalMessages(state)
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchComments.pending, (state) => {
        // Initialize object
        state.comments = {};
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        action.payload.forEach((comment) => {
          state.comments[comment.commentId] = comment;
        });
      })
      .addCase(fetchDeleteComment.fulfilled, (state, action) => {
        delete state.comments[action.payload.commentId];
      })
      .addCase(fetchEditComment.rejected, (state, action) => {
        state.modalErrorMessage = `Failed to update comment - ${
          action.payload?.message || action.payload?.comment
        }`;
      })
      .addCase(fetchCreateComment.rejected, (state, action) => {
        state.createErrorMessage = `Failed to create comment - ${
          action.payload?.message || action.payload?.comment
        }`;
      })
      .addMatcher(
        isAnyOf(fetchEditComment.fulfilled, fetchCreateComment.fulfilled),
        (state, action) => {
          state.comments[action.payload.commentId] = action.payload;
          state.modalSuccessMessage = 'Comment saved successfully';
        }
      )
      .addMatcher(
        isAnyOf(
          fetchCreateComment.pending,
          fetchEditComment.pending,
          fetchComments.pending,
          fetchDeleteComment.pending
        ),
        (state) => {
          clearModalMessages(state);
        }
      );
  }
});

export const { clearLinksModalMessages } = commentSlice.actions;

export const commentsReducer = commentSlice.reducer;
